import { Link } from '@tanstack/react-router';
import type { AuctionListItem } from '@/data/auction';
import { formatIDR } from '@/lib/format';
import { RarityBadge } from './RarityBadge';
import { CountdownTimer } from './CountdownTimer';

// ─── Fallback image SVG ───────────────────────────────────────────────────────

const FALLBACK_IMG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%231C1D1E" width="200" height="200"/><text x="100" y="110" text-anchor="middle" fill="%233E4142" font-size="14" font-family="serif">No Image</text></svg>';

// ─── Component ────────────────────────────────────────────────────────────────

interface AuctionCardProps {
  item: AuctionListItem;
}

/**
 * Clickable auction listing card. Navigates to `/auction/:id` on click.
 *
 * Displays: card photo, rarity badge, edition, item name, current highest bid,
 * total bids, and a live countdown timer.
 */
export function AuctionCard({ item }: AuctionCardProps) {
  return (
    <Link
      to="/auction/$auctionId"
      params={{ auctionId: item.id }}
      className="group block bg-iron-900 border border-iron-800 hover:border-secondary/40 transition-all duration-300 shadow-xl hover:shadow-secondary/10 relative overflow-hidden"
    >
      {/* Gold shimmer on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* ── Image ── */}
      <div className="relative overflow-hidden bg-iron-950 h-64 flex items-center justify-center border-b border-iron-800">
        <img
          src={item.image}
          alt={item.name}
          className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105 drop-shadow-[0_8px_20px_rgba(0,0,0,0.7)]"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMG;
          }}
        />
        {/* Rarity badge */}
        <div className="absolute top-3 left-3">
          <RarityBadge rarity={item.rarity} />
        </div>
        {/* Gold accent line */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
      </div>

      {/* ── Body ── */}
      <div className="p-6">
        {/* Edition */}
        <p className="text-[9px] tracking-[0.3em] text-iron-500 uppercase font-bold mb-2">
          {item.edition}
        </p>

        {/* Name */}
        <h3 className="font-epic text-xl tracking-widest text-iron-100 mb-5 group-hover:text-white transition-colors leading-tight">
          {item.name}
        </h3>

        <div className="h-px bg-iron-800 mb-5" />

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-[9px] tracking-[0.25em] uppercase text-iron-500 font-bold mb-1">
              Current Highest
            </p>
            <p className="font-epic text-secondary text-base font-bold tracking-wide">
              {formatIDR(item.currentHighest)}
            </p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.25em] uppercase text-iron-500 font-bold mb-1">
              Total Bids
            </p>
            <p className="font-epic text-iron-200 text-base font-bold">
              {item.totalBids}{' '}
              <span className="text-iron-500 text-xs font-sans">offers</span>
            </p>
          </div>
        </div>

        {/* Countdown */}
        <div className="bg-iron-950/60 border border-iron-800 px-4 py-3 flex items-center justify-between">
          <span className="text-[9px] tracking-[0.25em] uppercase text-iron-500 font-bold">
            Time Remaining
          </span>
          <CountdownTimer endsAt={item.endsAt} variant="inline" />
        </div>

        {/* CTA */}
        <div className="mt-4 flex justify-end">
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-500 group-hover:text-secondary transition-colors">
            Place Bid →
          </span>
        </div>
      </div>
    </Link>
  );
}
